import { useCallback, useEffect, useState } from 'react'

import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'

import { StyledError } from '../../../../theme/components/StyledError'
import { usePatchContactpersoonForEntity } from '../../hooks/usePatchContactpersoonForEntity'
import type { Contactpersoon } from '../../types/Contactpersoon'

interface Props {
  contactpersoon: Contactpersoon
  onClose: () => void
}

export function Edit({ contactpersoon, onClose }: Props) {
  const { mutate: patchContactpersoon, error } =
    usePatchContactpersoonForEntity()

  const [errors, setErrors] = useState({})
  const [naam, setNaam] = useState('')
  const [rol, setRol] = useState('')
  const [emailadres, setEmailadres] = useState('')
  const [telefoonnummer, setTelefoonnummer] = useState('')
  const [contactOrganisatie, setContactOrganisatie] = useState('')

  const setValues = useCallback((contactpersoon: Contactpersoon) => {
    const { naam, rol, emailadres, telefoonnummer, contactOrganisatie } =
      contactpersoon

    setNaam(naam)
    setRol(rol)
    setEmailadres(emailadres)
    setTelefoonnummer(telefoonnummer)
    setContactOrganisatie(contactOrganisatie || '')
  }, [])

  useEffect(() => {
    setValues(contactpersoon)
  }, [contactpersoon, setValues])

  const validate = () => {
    const naamError = !naam
    const rolError = !rol
    const emailadresError = !emailadres
    const telefoonnummerError = !telefoonnummer

    setErrors({
      naamError,
      rolError,
      emailadresError,
      telefoonnummerError,
    })

    return !(naamError || rolError || emailadresError || telefoonnummerError)
  }

  const onSubmitClick = () => {
    if (validate()) {
      patchContactpersoon(
        {
          entity: contactpersoon.entity,
          contactpersoon: {
            ...contactpersoon,
            emailadres,
            naam,
            rol,
            telefoonnummer,
            contactOrganisatie,
          },
        },
        { onSuccess: () => onClose() },
      )
    }
  }

  return (
    <>
      <DialogTitle>Bewerken {contactpersoon.naam}</DialogTitle>
      <DialogContent>
        <StyledError error={error} />
        <Box>
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            <AlertTitle>Let op!</AlertTitle>U bent ervoor verantwoordelijk om te
            melden dat contactgegevens worden verwerkt.
          </Alert>
        </Box>
        <Box>
          <TextField
            sx={{ pr: 1, pb: 1 }}
            onChange={(e) => setNaam(e.target.value)}
            value={naam}
            variant="standard"
            label="Naam"
            fullWidth
            error={errors['naamError']}
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />
          <TextField
            sx={{ pr: 1, pb: 1 }}
            onChange={(e) => setRol(e.target.value)}
            value={rol}
            variant="standard"
            label="Rol"
            fullWidth
            error={errors['rolError']}
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />
          <TextField
            sx={{ pr: 1, pb: 1 }}
            onChange={(e) => setContactOrganisatie(e.target.value)}
            value={contactOrganisatie || ''}
            variant="standard"
            label="Organisatie"
            fullWidth
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />
          <TextField
            sx={{ pr: 1, pb: 1 }}
            onChange={(e) => setTelefoonnummer(e.target.value)}
            value={telefoonnummer}
            variant="standard"
            label="Telefoonnummer"
            fullWidth
            error={errors['telefoonnummerError']}
            slotProps={{ htmlInput: { maxLength: 20 } }}
          />
          <TextField
            sx={{ pr: 1, pb: 1 }}
            onChange={(e) => setEmailadres(e.target.value)}
            value={emailadres}
            variant="standard"
            label="Emailadres"
            fullWidth
            error={errors['emailadresError']}
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" autoFocus>
          Annuleren
        </Button>
        <Button color="primary" onClick={onSubmitClick}>
          Opslaan
        </Button>
      </DialogActions>
    </>
  )
}
